// @greenlight-spec: 19-design-system.md
import { cn } from '@/lib/utils';

/**
 * Maps an array of numeric values to an SVG `points` attribute string.
 * X positions are evenly spaced across [0, width].
 * Y positions are normalized so min(data) → height (bottom) and max(data) → 0 (top).
 * Returns '' for an empty array. Single-point or all-equal arrays produce a flat
 * horizontal line at mid-height.
 */
export function sparklinePoints(data: number[], width = 100, height = 32): string {
  if (data.length === 0) return '';

  // No seed: reduce uses data[0] as the initial accumulator, safe because the
  // empty-array case is guarded above. Avoids a non-null assertion on data[0]
  // (typescript.md: guard, don't dodge noUncheckedIndexedAccess).
  const min = data.reduce((a, b) => (b < a ? b : a));
  const max = data.reduce((a, b) => (b > a ? b : a));
  const range = max - min;

  return data
    .map((v, i) => {
      const x = data.length === 1 ? width / 2 : (i / (data.length - 1)) * width;
      // When all values are equal, render a flat line at mid-height.
      const y = range === 0 ? height / 2 : height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');
}

export interface SparklineProps {
  data: number[];
  className?: string;
  ariaLabel?: string;
  filled?: boolean;
}

export function Sparkline({ data, className, ariaLabel, filled = false }: SparklineProps) {
  const points = sparklinePoints(data);

  // Build a closed polygon for the filled area: trace the line, drop to bottom-right,
  // then bottom-left. Only rendered when filled=true and there are at least 2 points.
  const areaPoints = filled && data.length >= 2 ? `${points} 100,32 0,32` : '';

  return (
    <svg
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      className={cn('w-full h-8', className)}
      aria-hidden={ariaLabel ? undefined : true}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
    >
      {ariaLabel && <title>{ariaLabel}</title>}
      {filled && areaPoints && <polygon points={areaPoints} className="fill-primary/10" />}
      {points && (
        <polyline
          points={points}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
